<template>
  <div>
    <header v-if="headingTitle" class="mt-4 xl:mb-16 mb-6">
      <Component
        :is="headingLevel"
        v-if="!!headingTitle"
        class="text-xl md:text-4xl text-black font-semibold text-balance"
      >
        {{ headingTitle }}
      </Component>

      <h2 v-if="!!headingSubTitle" class="text-base mt-4 md:text-2xl text-black/50">
        {{ headingSubTitle }}
      </h2>
    </header>

    <div
      v-if="!!title"
      class="bg-gradient-to-r p-4 overflow-hidden h-56"
      :class="computedGradients"
    >
      <div class="flex justify-between md:flex-row flex-col relative h-full">
        <div class="flex flex-col justify-between h-full items-start w-5/6 xl:w-4/6 z-10 gap-3">
          <div class="flex-grow gap-1 flex flex-col">
            <Component
              :is="titleLevel"
              v-if="!!title"
              class="text-lg md:text-4xl font-bold text-balance"
              :class="variant === 'empty' ? 'text-brand-black' : 'text-brand-white'"
            >
              {{ title }}
            </Component>
            <p class="text-base leading-5 text-balance" :class="variant === 'empty' ? 'text-brand-black' : 'text-brand-white'">
              {{ subtitle }}
            </p>
          </div>
          <template v-if="!!ctaLink">
            <div class="lg:flex hidden">
              <BaseButtonLink
                :to="ctaLink"
                variant="secondary-outlined"
              >
                {{ ctaText }}
              </BaseButtonLink>
            </div>
            <NuxtLink
              :to="ctaLink"
              external
              class="lg:hidden flex items-center justify-center underline "
              :class="variant === 'empty' ? 'text-brand-black' : 'text-brand-white'"
            >
              {{ ctaText }}
            </NuxtLink>
          </template>
        </div>

        <div class="absolute md:top-1 top-4 w-[220px] md:w-[320px] xl:w-[420px] md:-right-20 -right-20 z-0 hover:scale-110 duration-500 ease-in-out">
          <NuxtImg
            v-if="!!image"
            class="object-contain max-h-[220px]"
            :alt="image?.title || ''"
            :src="image?.meta?.download_url || ''"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { HeadingLevels, WagtailImage } from '~/types';
import BaseButtonLink from '~/components/BaseButtonLink.vue';

interface Props {
  title: string | null
  subtitle?: string
  titleLevel?: HeadingLevels
  headingTitle?: string,
  headingSubTitle?: string,
  headingLevel?: HeadingLevels,
  ctaText?: string
  ctaLink?: string
  image?: WagtailImage
  variant?: 'default' | 'primary' | 'modern' | 'elegant' | 'empty'
}

const props = withDefaults(defineProps<Props>(), {
  titleLevel: 'h1',
  variant: 'primary',
  subtitle: undefined,
  ctaText: undefined,
  ctaLink: undefined,
  image: undefined,
  headingTitle: undefined,
  headingSubTitle: undefined,
  headingLevel: 'h2',
});

const computedGradients = computed(() => {
  const gradientsMapping = {
    empty: 'border',
    default: 'from-brand-black to-brand-black/40',
    primary: 'from-brand-primary to-brand-primary/40',
    modern: 'from-purple-600 to-blue-600',
    elegant: 'from-gray-600 to-gray-200',
  };

  return gradientsMapping[props.variant];
});
</script>
